import React, { useState, useEffect, useMemo } from 'react';
import { Reorder, useDragControls, AnimatePresence, motion } from 'framer-motion';
import { GripVertical, Check, Plus, ListTodo, RotateCcw, Clock } from 'lucide-react';
import { T, ROLES, springs } from '../../lib/tokens';
import { supabase } from '../../lib/supabase';
import { RoleBadge } from '../primitives/Badge';
import { SectionHeader } from './SectionHeader';
import { Glass } from '../glass/Glass';
import { DeferMenu } from './DeferMenu';
import { completeTask, applyManualOrder, clearManualOrder } from '../../lib/taskActions';

const fmtDuration = (min) => {
  if (!min) return '';
  if (min >= 60) return `${Math.floor(min / 60)}h${min % 60 ? ` ${min % 60}m` : ''}`;
  return `${min}m`;
};

// ─── Queue row ───────────────────────────────────────────────────────────────
const QueueRow = ({ task, index, justDone, onToggle, onDeferred, onDragEnd }) => {
  const controls = useDragControls();
  const role = task.roles || task.goals?.roles;
  const done = justDone.has(task.id);
  const isOverdue = task.deadline && new Date(task.deadline) < new Date() && !done;
  const dueToday = !isOverdue && task.deadline &&
    new Date(task.deadline).toDateString() === new Date().toDateString();

  return (
    <Reorder.Item
      value={task}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onDragEnd}
      whileDrag={{ scale: 1.02, boxShadow: T.shadowLg, background: 'rgba(255,255,255,0.95)' }}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '9px 10px', borderRadius: 10, listStyle: 'none',
        background: done ? 'rgba(90,158,120,0.05)' : 'rgba(255,255,255,0.65)',
        border: `1px solid ${done ? 'rgba(90,158,120,0.18)' : 'rgba(255,255,255,0.85)'}`,
        position: 'relative',
      }}
    >
      <div
        onPointerDown={(e) => { e.preventDefault(); controls.start(e); }}
        title="Drag to reorder"
        style={{ cursor: 'grab', display: 'flex', alignItems: 'center', touchAction: 'none', color: T.textLight, flexShrink: 0 }}
      >
        <GripVertical size={13} />
      </div>

      <span style={{
        fontSize: 10, fontWeight: 700, color: T.textLight, width: 14, textAlign: 'center',
        flexShrink: 0, fontFamily: "'SF Mono', Menlo, Monaco, monospace",
      }}>{index + 1}</span>

      <button onClick={() => onToggle(task)} title={done ? 'Undo' : 'Mark done'} style={{
        width: 18, height: 18, borderRadius: 5, flexShrink: 0,
        border: `1.5px solid ${done ? T.green : 'rgba(0,0,0,0.18)'}`,
        background: done ? T.greenLight : 'transparent',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.15s ease',
      }}>
        {done && <Check size={11} color={T.green} />}
      </button>

      <span style={{
        flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, color: T.text, lineHeight: 1.3,
        textDecoration: done ? 'line-through' : 'none', opacity: done ? 0.55 : 1,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{task.title}</span>

      {isOverdue && (
        <span style={{ fontSize: 9, fontWeight: 600, color: T.red, padding: '1px 5px', borderRadius: 3, background: T.redLight, flexShrink: 0 }}>overdue</span>
      )}
      {dueToday && (
        <span style={{ fontSize: 9, fontWeight: 600, color: T.amber, padding: '1px 5px', borderRadius: 3, background: T.amberLight, flexShrink: 0 }}>today</span>
      )}
      {task.priority === 1 && !done && (
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.red, flexShrink: 0 }} title="Urgent" />
      )}
      {role?.slug && <RoleBadge roleSlug={role.slug} small />}
      {task.duration_minutes > 0 && (
        <span style={{ fontSize: 10, color: T.textMuted, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Clock size={9} />{fmtDuration(task.duration_minutes)}
        </span>
      )}
      <DeferMenu task={task} onDeferred={() => onDeferred(task)} />
    </Reorder.Item>
  );
};

// ─── Up Next queue ───────────────────────────────────────────────────────────
// Today's execution order, directly manipulable: drag to reorder (persists as
// tasks.sort_order 1..n — the scheduler packs manual ranks first), tick to
// complete, clock to defer. Quick-add drops a task straight into today.
export const UpNextQueue = ({ tasks, onUpdate, triggerScheduler }) => {
  const [items, setItems] = useState(tasks);
  const [justDone, setJustDone] = useState(new Set());
  const [adding, setAdding] = useState('');
  const [busy, setBusy] = useState(false);
  const itemsRef = React.useRef(items);
  itemsRef.current = items;

  // Resync local order whenever the upstream queue changes (refetch landed).
  const signature = useMemo(() => tasks.map(t => t.id).join(','), [tasks]);
  useEffect(() => { setItems(tasks); setJustDone(new Set()); }, [signature]); // eslint-disable-line

  const refresh = () => { onUpdate && onUpdate(); triggerScheduler && triggerScheduler(); };

  // Persist on drop, and only if the order actually moved.
  const handleDragEnd = async () => {
    const ordered = itemsRef.current;
    if (ordered.map(t => t.id).join(',') === tasks.map(t => t.id).join(',')) return;
    await applyManualOrder(ordered.filter(t => !justDone.has(t.id)));
    refresh();
  };

  const toggle = async (task) => {
    const newDone = !justDone.has(task.id);
    setJustDone(prev => {
      const next = new Set(prev);
      newDone ? next.add(task.id) : next.delete(task.id);
      return next;
    });
    await completeTask(task, newDone);
    refresh();
  };

  const hasManual = items.some(t => t.sort_order >= 1);
  const resetOrder = async () => {
    await clearManualOrder(items);
    refresh();
  };

  const quickAdd = async () => {
    const title = adding.trim();
    if (!title || busy) return;
    setBusy(true);
    // Same [30m]/[2h] suffix convention as the Tasks view quick-add.
    const effortMatch = title.match(/\[(\d+[hm](?:\s*\d+[hm])?)\]$/i);
    const effort = effortMatch ? effortMatch[1] : null;
    const cleanTitle = effort ? title.replace(/\s*\[.*\]$/, '') : title;
    let duration_minutes = 30;
    if (effort) {
      const h = effort.match(/(\d+)h/);
      const m = effort.match(/(\d+)m/);
      duration_minutes = (h ? parseInt(h[1]) * 60 : 0) + (m ? parseInt(m[1]) : 0);
    }
    await supabase.from('tasks').insert({
      title: cleanTitle,
      priority: 2,
      effort: effort || null,
      duration_minutes: duration_minutes || 30,
      done: false,
      status: 'todo',
      auto_schedule: true,
      sort_order: 0,
      due_label: 'Today',
      deadline: new Date(new Date().setHours(18, 0, 0, 0)).toISOString(),
    });
    setAdding('');
    setBusy(false);
    refresh();
  };

  const totalMin = items.filter(t => !justDone.has(t.id)).reduce((acc, t) => acc + (t.duration_minutes || 30), 0);

  return (
    <Glass style={{ padding: 20 }} hover={false} depth={2}>
      <SectionHeader
        icon={ListTodo} iconBg={T.amberLight} iconColor={T.amber}
        title="Up Next"
        subtitle={items.length === 0 ? 'Nothing queued for today' : `${items.length} task${items.length === 1 ? '' : 's'} · ~${fmtDuration(totalMin)}`}
        right={hasManual ? (
          <button onClick={resetOrder} title="Back to automatic order" style={{
            display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px',
            borderRadius: 6, border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.03)',
            cursor: 'pointer', fontSize: 10, fontWeight: 600, color: T.textMid, fontFamily: 'inherit',
          }}>
            <RotateCcw size={10} /> auto order
          </button>
        ) : (
          <span style={{ fontSize: 10, fontWeight: 600, color: T.green, letterSpacing: '0.04em' }}>AUTO</span>
        )}
      />

      {items.length > 0 && (
        <Reorder.Group axis="y" values={items} onReorder={setItems}
          style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 0, margin: 0 }}
        >
          <AnimatePresence>
            {items.map((task, i) => (
              <QueueRow
                key={task.id}
                task={task}
                index={i}
                justDone={justDone}
                onToggle={toggle}
                onDeferred={() => refresh()}
                onDragEnd={handleDragEnd}
              />
            ))}
          </AnimatePresence>
        </Reorder.Group>
      )}

      {/* Quick add */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', marginTop: 8,
        borderRadius: 10, background: 'rgba(255,255,255,0.4)', border: '1px dashed rgba(0,0,0,0.10)',
      }}>
        <Plus size={13} color={T.textLight} style={{ flexShrink: 0 }} />
        <input
          value={adding}
          onChange={e => setAdding(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') quickAdd(); }}
          placeholder="Add for today… [30m]"
          disabled={busy}
          style={{
            flex: 1, border: 'none', background: 'transparent', fontSize: 12.5,
            color: T.text, outline: 'none', fontFamily: 'inherit', opacity: busy ? 0.5 : 1,
          }}
        />
        {adding.trim() && (
          <button onClick={quickAdd} disabled={busy} style={{
            padding: '3px 10px', borderRadius: 6, border: 'none', background: T.amber,
            color: '#fff', cursor: 'pointer', fontSize: 10, fontWeight: 600, fontFamily: 'inherit',
          }}>Add</button>
        )}
      </div>
    </Glass>
  );
};
